/**
 * CodeNode - Secure Code Execution Node
 *
 * A node for writing and executing Python/JavaScript code in isolated
 * Cloudflare Sandbox containers. Features:
 * - CodeMirror editor with syntax highlighting
 * - Language selection (Python/JavaScript)
 * - Execution controls (Run button)
 * - Status indicator (idle/running/complete/error)
 * - Rich output rendering (matplotlib charts, pandas DataFrames)
 * - Export functionality (PNG for charts, CSV for tables)
 *
 * Design follows GeneratorNode patterns:
 * - Multi-layered shadows for depth
 * - Floating title above card
 * - Emerald/teal gradient buttons
 * - Gray backgrounds for input areas
 * - Status chips with colored backgrounds
 */

import { memo, useState, useCallback, useEffect, useRef } from 'react';
import { Handle, Position, NodeResizer, type NodeProps, type Node } from '@xyflow/react';
import type { CodeNodeData, CodeLanguage, ExecutionStatus, ExecutionResult, ExecutionError, RichOutput } from '@deep-agents/shared-types';
import { getLanguageInfo } from '@deep-agents/shared-types';
import { CodeEditor } from './CodeEditor';
import { LanguageSelector } from './LanguageSelector';
import { RichOutputPanel } from './RichOutputPanel';
import { TerminalOutput, type TerminalOutputHandle } from './TerminalOutput';
import { ExecutionHistory } from './ExecutionHistory';
import { ErrorSuggestion } from './ErrorSuggestion';
import { useCodeExecution } from '../../../hooks/useCodeExecution';

// ============================================================================
// TYPES
// ============================================================================

type CodeNodeType = Node<CodeNodeData, 'code'>;

// ============================================================================
// SHADOW HELPER
// ============================================================================

/**
 * Get shadow style based on node state
 * Matches GeneratorNode and other nodes for consistency
 */
function getShadowStyle(dragging: boolean, selected: boolean): string {
  if (dragging) {
    return 'var(--shadow-elevation-high)';
  }
  if (selected) {
    return 'var(--shadow-elevation-medium)';
  }
  return 'var(--shadow-elevation-low)';
}

// ============================================================================
// STATUS COLORS
// ============================================================================

/**
 * Status indicator color mappings
 * Matches the color scheme from GeneratorNode
 */
const STATUS_COLORS = {
  idle: 'bg-gray-100 text-gray-600',
  running: 'bg-indigo-100 text-indigo-600',
  complete: 'bg-green-100 text-green-600',
  error: 'bg-red-100 text-red-600',
} as const;

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * CodeNode Component
 *
 * Main component for code editing and execution on the canvas.
 *
 * Props come from React Flow's node data structure.
 * Execution will be triggered via API call (to be implemented in future plans).
 */
export const CodeNode = memo(({ id, data, selected, dragging }: NodeProps<CodeNodeType>) => {
  // ==================== DATA DESTRUCTURING ====================

  const {
    title = '',
    code = '# Python code\nprint("Hello, World!")\n',
    language = 'python',
    status: propsStatus = 'idle',
    currentResult,
    onTitleChange,
    onCodeChange,
    onLanguageChange,
    onExecute,
    // Runtime context (injected by BoardCanvas)
    // ydoc,
    boardId,
    // nodesMap,
  } = data;

  // ==================== LOCAL STATE ====================

  // Local title state (synced to parent on change)
  const [localTitle, setLocalTitle] = useState(title);

  // Local code state (synced to parent on change)
  const [localCode, setLocalCode] = useState(code);

  // Local language state (synced to parent on change)
  const [localLanguage, setLocalLanguage] = useState<CodeLanguage>(language);

  // Hover state for showing/hiding controls
  const [isHovered, setIsHovered] = useState(false);

  // ==================== EXECUTION HISTORY & ERROR STATE ====================

  // History of all executions in this session
  const [executionHistory, setExecutionHistory] = useState<ExecutionResult[]>([]);

  // Whether to show the history panel
  const [showHistory, setShowHistory] = useState(false);

  // Current error (for AI fix suggestions)
  const [currentError, setCurrentError] = useState<ExecutionError | null>(null);

  // Rich outputs from the current execution
  const [richOutputs, setRichOutputs] = useState<RichOutput[]>([]);

  // ==================== TERMINAL REF ====================

  // Ref to the terminal output component for writing stdout/stderr
  const terminalRef = useRef<TerminalOutputHandle>(null);

  // ==================== CODE EXECUTION HOOK ====================

  // Use the code execution hook to handle API calls and streaming
  const {
    execute: executeCode,
    cancel: cancelExecution,
    status: executionStatus,
    error: executionError,
  } = useCodeExecution({
    boardId: boardId || '',
    // Write stdout to terminal (raw text, no newline added)
    onStdout: (data) => {
      terminalRef.current?.write(data);
    },
    // Write stderr to terminal in red (ANSI escape code for red text)
    onStderr: (data) => {
      terminalRef.current?.write(`\x1b[31m${data}\x1b[0m`);
    },
    // Handle execution result
    onResult: (result) => {
      // Add to execution history for scrollback
      setExecutionHistory(prev => [...prev, result]);

      // Store rich outputs for display
      if (result.results && result.results.length > 0) {
        setRichOutputs(result.results);
      }

      // Handle errors - display in terminal and set for AI suggestions
      if (result.error) {
        setCurrentError(result.error);
        terminalRef.current?.writeLine(
          `\x1b[31m${result.error.name}: ${result.error.value}\x1b[0m`
        );
      } else {
        // Clear error if execution succeeded
        setCurrentError(null);
      }
    },
  });

  // Combine props status with local execution status
  // This allows both external control (props) and internal execution
  const status: ExecutionStatus = executionStatus !== 'idle' ? executionStatus : propsStatus;

  // ==================== SYNC EFFECTS ====================

  // Sync local state with props when they change (from Yjs updates)
  // This ensures collaboration works - if another user changes the code, we see it
  useEffect(() => {
    setLocalTitle(title);
  }, [title]);

  useEffect(() => {
    setLocalCode(code);
  }, [code]);

  useEffect(() => {
    setLocalLanguage(language);
  }, [language]);

  // ==================== HANDLERS ====================

  /**
   * Handle title changes
   */
  const handleTitleChange = useCallback(
    (newTitle: string) => {
      setLocalTitle(newTitle);
      onTitleChange?.(newTitle);
    },
    [onTitleChange]
  );

  /**
   * Handle code changes from the editor
   */
  const handleCodeChange = useCallback(
    (newCode: string) => {
      setLocalCode(newCode);
      onCodeChange?.(newCode);
    },
    [onCodeChange]
  );

  /**
   * Handle language selection changes
   */
  const handleLanguageChange = useCallback(
    (newLanguage: CodeLanguage) => {
      const previousLanguage = localLanguage;
      const previousDefault = getLanguageInfo(previousLanguage).defaultCode;
      const nextDefault = getLanguageInfo(newLanguage).defaultCode;

      setLocalLanguage(newLanguage);
      onLanguageChange?.(newLanguage);

      // If the editor still has the previous default (or is empty),
      // swap in the new language's default template.
      const isDefault =
        localCode.trim() === '' ||
        localCode === previousDefault;
      if (isDefault) {
        setLocalCode(nextDefault);
        onCodeChange?.(nextDefault);
      }
    },
    [localCode, localLanguage, onCodeChange, onLanguageChange]
  );

  /**
   * Handle execute button click
   * Clears terminal and starts code execution via streaming API
   */
  const handleExecute = useCallback(() => {
    if (status === 'running') return; // Prevent double-click during execution

    // Clear terminal and previous results before new execution
    terminalRef.current?.clear();
    setRichOutputs([]);
    setCurrentError(null);

    // Execute the code via the streaming API
    executeCode(localCode, localLanguage);

    // Also notify parent (for Yjs sync, history tracking, etc.)
    onExecute?.();
  }, [status, localCode, localLanguage, executeCode, onExecute]);

  /**
   * Handle stop button click
   * Cancels the running execution
   */
  const handleCancel = useCallback(() => {
    cancelExecution();
  }, [cancelExecution]);

  /**
   * Handle re-run from history
   * Loads historical code into editor and executes it
   */
  const handleRerun = useCallback((code: string) => {
    // Update local code state
    setLocalCode(code);

    // Clear terminal and outputs for new execution
    terminalRef.current?.clear();
    setRichOutputs([]);
    setCurrentError(null);

    // Execute the code
    executeCode(code, localLanguage);
  }, [executeCode, localLanguage]);

  /**
   * Handle apply fix from error suggestion
   * Puts the suggested code in the editor for user review (does not auto-run)
   */
  const handleApplyFix = useCallback((fixedCode: string) => {
    setLocalCode(fixedCode);
    onCodeChange?.(fixedCode);
    // Don't auto-run - let user review the fix first
  }, [onCodeChange]);

  // ==================== COMPUTED VALUES ====================

  // Show controls when hovered OR selected
  const showControls = isHovered || selected;

  // Format status for display
  const statusLabel = status.charAt(0).toUpperCase() + status.slice(1);

  // ==================== RENDER ====================

  return (
    <div
      className="relative transition-all duration-300"
      style={{ padding: '0 8px' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* ==================== CONNECTION HANDLES ==================== */}
      {/* Left handle - receives data from upstream nodes */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="!w-4 !h-4 !bg-slate-400 !border-2 !border-white !opacity-100"
        style={{ top: '50%', pointerEvents: 'all', zIndex: 100 }}
        title="Receive data from upstream nodes"
      />
      {/* Right handle - sends results to downstream nodes */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="!w-4 !h-4 !bg-emerald-500 !border-2 !border-white !opacity-100"
        style={{ top: '50%', pointerEvents: 'all', zIndex: 100 }}
        title="Send execution results to downstream nodes"
      />

      {/* ==================== FLOATING HEADER (Above Card) ==================== */}
      {/* Positioned absolutely so it doesn't affect node bounding box */}
      {/* Only visible on hover or when selected */}
      <div
        className={`
          absolute bottom-full left-0 right-0 pb-2
          transition-opacity duration-200
          ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
      >
        {/* Title Input */}
        <div className="flex items-center gap-2 px-1">
          <div
            className="nodrag nopan"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="text"
              value={localTitle}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Untitled Code"
              className="
                px-2.5 py-1 text-[12px] font-semibold
                bg-gradient-to-r from-emerald-50 to-teal-50
                rounded-lg border border-emerald-100/50
                outline-none focus:border-emerald-300 focus:ring-1 focus:ring-emerald-200
                placeholder:text-emerald-300 placeholder:font-medium
                min-w-[80px] max-w-[200px] truncate
                transition-all
                text-emerald-700
              "
              style={{ width: `${Math.max(80, Math.min(200, (localTitle.length || 12) * 8))}px` }}
            />
          </div>
        </div>
      </div>

      {/* ==================== MAIN CARD CONTAINER ==================== */}
      {/* This container is draggable by default (no nodrag) */}
      <div
        className="
          relative bg-white rounded-2xl overflow-visible
          border transition-all duration-300
          border-neutral-200/60
        "
        style={{
          // Dynamic shadow based on drag/select state
          boxShadow: getShadowStyle(dragging || false, selected || false),
          // Smooth shadow transition + scale effect
          transition: dragging
            ? 'box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            : 'box-shadow 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275), transform 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          // Slight scale up when dragging
          transform: dragging ? 'scale(1.01)' : 'scale(1)',
          // Minimum dimensions for code editing
          minWidth: 500,
          minHeight: 400,
        }}
      >
        {/* ==================== RESIZE HANDLES ==================== */}
        <NodeResizer
          minWidth={500}
          minHeight={400}
          isVisible={selected || false}
          lineClassName="border-emerald-500"
          handleClassName="bg-emerald-500 border-2 border-white rounded"
        />

        {/* ==================== CONTENT AREA ==================== */}
        <div
          className="relative px-5 pt-5 pb-4 overflow-hidden flex flex-col gap-4"
          style={{ height: '100%', zIndex: 1 }}
        >
          {/* ==================== LANGUAGE SELECTOR ==================== */}
          <LanguageSelector
            language={localLanguage}
            onLanguageChange={handleLanguageChange}
            disabled={status === 'running'}
          />

          {/* ==================== CODE EDITOR ==================== */}
          <div className="flex-1 min-h-[200px]">
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Code
            </label>
            <CodeEditor
              code={localCode}
              language={localLanguage}
              onChange={handleCodeChange}
              onExecute={handleExecute}
              readOnly={status === 'running'}
              placeholder={`Enter your ${localLanguage === 'python' ? 'Python' : 'JavaScript'} code here...`}
              minHeight="200px"
              maxHeight="300px"
            />
            <p className="mt-1 text-[10px] text-gray-400">
              Press Cmd/Ctrl+Enter to run
            </p>
          </div>

          {/* ==================== EXECUTION CONTROLS ==================== */}
          <div className="flex items-center justify-between gap-3 nodrag nopan">
            {/* Run/Stop Button */}
            {status === 'running' ? (
              // Stop button when running
              <button
                onClick={handleCancel}
                className="
                  flex-1 flex items-center justify-center gap-2 px-4 py-2.5
                  text-[14px] font-semibold rounded-lg
                  bg-red-500 text-white hover:bg-red-600 shadow-md hover:shadow-lg
                  transition-all
                "
              >
                {/* Stop icon */}
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <rect x="6" y="6" width="12" height="12" rx="1" />
                </svg>
                Stop
              </button>
            ) : (
              // Run button when not running
              <button
                onClick={handleExecute}
                disabled={!localCode.trim()}
                className={`
                  flex-1 flex items-center justify-center gap-2 px-4 py-2.5
                  text-[14px] font-semibold rounded-lg
                  transition-all
                  ${
                    !localCode.trim()
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600 shadow-md hover:shadow-lg'
                  }
                `}
              >
                {/* Play icon */}
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Run Code
              </button>
            )}

            {/* Status Indicator */}
            <div
              className={`px-3 py-2 rounded-lg text-[11px] font-semibold ${STATUS_COLORS[status]}`}
            >
              {statusLabel}
            </div>
          </div>

          {/* ==================== TERMINAL OUTPUT ==================== */}
          {/* Streaming output display for stdout/stderr */}
          <div className="mt-2">
            <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
              Output
            </div>
            <TerminalOutput ref={terminalRef} className="h-32" />
          </div>

          {/* ==================== ERROR DISPLAY WITH AI SUGGESTIONS ==================== */}
          {/* Show execution hook error as simple text */}
          {executionError && !currentError && (
            <div className="p-3 bg-red-50 rounded-lg border border-red-100">
              <p className="text-[12px] font-semibold text-red-700">
                {executionError}
              </p>
            </div>
          )}

          {/* Show code execution error with AI fix suggestions */}
          {currentError && (
            <ErrorSuggestion
              error={currentError}
              code={localCode}
              language={localLanguage}
              onApplyFix={handleApplyFix}
              boardId={boardId || ''}
            />
          )}

          {/* ==================== EXECUTION RESULTS ==================== */}
          {/* Shown when execution completes successfully */}
          {status === 'complete' && currentResult && (
            <div className="p-3 bg-green-50 rounded-lg border border-green-100">
              <p className="text-[11px] font-semibold text-green-700 mb-2">
                Execution Complete ({currentResult.durationMs}ms)
              </p>
              {/* Stdout display */}
              {currentResult.stdout.length > 0 && (
                <div className="mb-2">
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
                    Output
                  </p>
                  <pre className="text-[12px] text-gray-800 bg-white p-2 rounded border border-gray-200 overflow-auto max-h-[150px] whitespace-pre-wrap font-mono">
                    {currentResult.stdout.join('\n')}
                  </pre>
                </div>
              )}
            </div>
          )}

          {/* ==================== RICH OUTPUTS (Charts, Tables) ==================== */}
          {/* Displays matplotlib charts, pandas DataFrames, and other rich content */}
          {richOutputs.length > 0 && (
            <RichOutputPanel outputs={richOutputs} />
          )}

          {/* ==================== EXECUTION HISTORY ==================== */}
          {/* Toggle button and collapsible history panel */}
          <div className="mt-2 nodrag nopan">
            {/* History toggle button */}
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              {/* Chevron icon - rotates when expanded */}
              <svg
                className={`w-3 h-3 transition-transform ${showHistory ? 'rotate-90' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              History ({executionHistory.length})
            </button>

            {/* Collapsible history panel */}
            {showHistory && (
              <div className="mt-2">
                <ExecutionHistory
                  history={executionHistory}
                  onRerun={handleRerun}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

// Display name for React DevTools
CodeNode.displayName = 'CodeNode';

// Export as default as well for convenience
export default CodeNode;
